import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Button, CircularProgress, Container, Grid, Stack, Switch, TextField, Typography } from '@mui/material';

import {
  AssetMetadata,
  SearchAssetsResponse,
  SearchOutputVideosResponse,
  VideoMetadata,
  getSignedUrls,
  search,
} from '../../services/VideoGenService';

import CreateAssetModal from './CreateAssetModal';
import CreateVideoPage from './CreateVideoPage';
import MediaOverviewCard from './MediaOverviewCard';

interface MediaItem {
  id: string;
  templateName: string;
  videoUrl: string;
  metadata: AssetMetadata | VideoMetadata;
}

export default function MediaViewPage() {
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);
  const [showOutputVideos, setShowOutputVideos] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [createVideoModalOpen, setCreateVideoModalOpen] = useState(false);
  const [mediaItems, setMediaItems] = useState<Array<MediaItem>>([]);
  const [loading, setLoading] = useState(false);
  const [idsSearch, setIdsSearch] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();

  const [offerIdsSearch, setOfferIdsSearch] = useState<string>('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const loadMediaItems = useCallback(
    async (resetList = false, forceShowOutputVideos?: boolean) => {
      setLoading(true);
      try {
        const currentPage = resetList ? 1 : page;
        const searchParams: Record<string, any> = {
          page: currentPage,
          limit: 10,
        };

        const filteredIdsSearch = idsSearch
          .split(',')
          .map((id) => id.trim())
          .filter((id) => id.length > 0);
        const filteredOfferIdsSearch = offerIdsSearch
          .split(',')
          .map((id) => id.trim())
          .filter((id) => id.length > 0);
        if (filteredIdsSearch.length > 0) {
          searchParams.ids = filteredIdsSearch;
        }
        if (filteredOfferIdsSearch.length > 0) {
          searchParams.offerIds = filteredOfferIdsSearch;
        }

        const currentShowOutputVideos = forceShowOutputVideos !== undefined ? forceShowOutputVideos : showOutputVideos;
        const searchType = currentShowOutputVideos ? 'videos' : 'assets';
        searchParams.status = currentShowOutputVideos ? ['SUCCEEDED', 'QUEUED', 'GENERATING', 'FAILED'] : ['ACTIVE'];
        const searchResponse = await search(searchType, searchParams);
        if (searchResponse.result.length === 0) {
          setHasMore(false);
          if (resetList) {
            setMediaItems([]);
          }
          return;
        }
        const ids = searchResponse.result.map(
          (item: SearchAssetsResponse['result'][number] | SearchOutputVideosResponse['result'][number]) => item.id,
        );
        const signedUrlsResponse = await getSignedUrls(searchType, ids);
        const items: Array<MediaItem> = searchResponse.result.map((item: any) => ({
          id: item.id,
          templateName: item.name || item.templateName || `${searchType.slice(0, -1)} ${item.id}`,
          videoUrl: signedUrlsResponse.result[item.id] || '',
          metadata: item,
        }));

        if (resetList) {
          setMediaItems(items);
          setPage(1);
        } else {
          setMediaItems((prev) => [...prev, ...items]);
          setPage((prev) => prev + 1);
        }

        setHasMore(searchResponse.result.length === 10);
      } catch (error) {
        enqueueSnackbar('Failed to load media items', { variant: 'error' });
        console.error('Error loading media items:', error);
      } finally {
        setLoading(false);
      }
    },
    [page, idsSearch, offerIdsSearch, showOutputVideos, enqueueSnackbar],
  );

  // Load initial data
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    loadMediaItems(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOutputVideos]);

  // Auto scroll implementation
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1000 &&
        !loading &&
        hasMore
      ) {
        loadMediaItems();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, hasMore, loadMediaItems]);

  const handleVideoPlay = (videoId: string) => {
    setPlayingVideoId(videoId);
  };

  const handleUpload = () => {
    setModalOpen(false);
    loadMediaItems(true);
  };

  const handleUploadComplete = useCallback(() => {
    setPage(1);
    setHasMore(true);
    loadMediaItems(true);
  }, [loadMediaItems]);

  const handleCreateVideo = () => {
    setCreateVideoModalOpen(true);
  };

  const handleVideoGenerate = () => {
    loadMediaItems(true);
  };

  const handleSearch = () => {
    setPage(1);
    setHasMore(true);
    setMediaItems([]);
    loadMediaItems(true);
  };

  const handleSwitchChange = (checked: boolean) => {
    setShowOutputVideos(checked);
    setIdsSearch('');
    setOfferIdsSearch('');
  };

  return (
    <Container>
      <Stack direction="row" spacing={2} mb={2}>
        <Button variant="contained" onClick={handleCreateVideo}>
          Create Video
        </Button>
        <Button variant="outlined" onClick={() => setModalOpen(true)}>
          Upload Assets
        </Button>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography>Assets</Typography>
          <Switch
            checked={showOutputVideos}
            onChange={(e) => handleSwitchChange(e.target.checked)}
            color="primary"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: 'primary.main',
              },
              '& .MuiSwitch-switchBase': {
                color: 'primary.main',
              },
              '& .MuiSwitch-track': {
                backgroundColor: 'primary.main',
              },
            }}
          />
          <Typography>Videos</Typography>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={2} mb={3}>
        <TextField
          fullWidth
          placeholder="Search by IDs... (Separated by comma)"
          size="small"
          value={idsSearch}
          onChange={(e) => setIdsSearch(e.target.value)}
        />
        <TextField
          fullWidth
          placeholder="Search by Offer ID... (Separated by comma)"
          size="small"
          value={offerIdsSearch}
          onChange={(e) => setOfferIdsSearch(e.target.value)}
        />
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
      </Stack>
      <Grid container spacing={2}>
        {mediaItems.map((item) => (
          <Grid item xs={12} sm={6} md={6} lg={4} key={item.id}>
            <MediaOverviewCard
              status={item.metadata.status}
              mediaType={showOutputVideos ? 'VIDEO' : (item.metadata as AssetMetadata).type}
              metadata={item.metadata}
              type={showOutputVideos ? 'output' : 'asset'}
              templateName={item.templateName}
              videoUrl={item.videoUrl}
              isPlaying={playingVideoId === item.id}
              onPlay={() => handleVideoPlay(item.id)}
              videoId={item.id}
            />
          </Grid>
        ))}
      </Grid>
      {loading && (
        <Stack direction="row" justifyContent="center" mt={3}>
          <CircularProgress />
        </Stack>
      )}
      <CreateAssetModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onUpload={handleUpload}
        onUploadComplete={handleUploadComplete}
      />
      <CreateVideoPage
        open={createVideoModalOpen}
        onClose={() => setCreateVideoModalOpen(false)}
        onGenerate={handleVideoGenerate}
      />
    </Container>
  );
}
